import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { CONTACT } from '../data/contact';
import { submitLead } from '../utils/submitLead';
import './ContactModal.css';

const ContactModal = ({ isOpen, onClose }) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    closeBtnRef.current?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const list = Array.from(focusable);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, isSubmitting, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Name is required';
    if (!formData.email.trim()) {
      next.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      next.email = 'Enter a valid email';
    }
    if (!formData.message.trim()) next.message = 'Message is required';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await submitLead({
        ...formData,
        source: 'contact-modal',
        subject: 'LogitsLab contact modal',
      });
      setSubmittedEmail(formData.email);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => {
        setIsSubmitted(false);
        onClose();
      }, 2600);
    } catch (error) {
      setErrors({
        submit: error.message || `Failed to send. Email us at ${CONTACT.email}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setFormData({ name: '', email: '', phone: '', message: '' });
    setErrors({});
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose} role="presentation">
      <div
        ref={dialogRef}
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="modal-close"
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label="Close"
        >
          <FaTimes />
        </button>

        <div className="modal-header">
          <h2 id="contact-modal-title">Start a conversation</h2>
          <p>Tell us what you are building. We typically reply within a day.</p>
        </div>

        {isSubmitted ? (
          <div className="success-state">
            <FaCheckCircle className="success-icon" />
            <h3>Message sent</h3>
            <p>We will follow up at {submittedEmail}</p>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="modal-name">Name</label>
              <input
                type="text"
                id="modal-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'input-error' : ''}
                disabled={isSubmitting}
                placeholder="Your name"
                autoComplete="name"
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="modal-email">Email</label>
              <input
                type="email"
                id="modal-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'input-error' : ''}
                disabled={isSubmitting}
                placeholder="you@company.com"
                autoComplete="email"
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="modal-phone">Phone (optional)</label>
              <input
                type="tel"
                id="modal-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder={CONTACT.phone}
                autoComplete="tel"
              />
            </div>

            <div className="form-group">
              <label htmlFor="modal-message">Message</label>
              <textarea
                id="modal-message"
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                className={errors.message ? 'input-error' : ''}
                disabled={isSubmitting}
                placeholder="What should we help you ship?"
              />
              {errors.message && (
                <span className="error-text">{errors.message}</span>
              )}
            </div>

            {errors.submit && (
              <div className="error-message">{errors.submit}</div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="spinner" />
                    Sending…
                  </>
                ) : (
                  'Send message'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
